import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import './ecm-table.scss';
import ConnectionTypeCell from './connection-type-cell/connection-type-cell';
import ConnectionPlanCell from './connection-plan-cell/connection-plan-cell';
import ConnectionPolicyInfoCell from './connection-policy-info-cell/connection-policy-info-cell';
import ConnectionAppliedInfoCell from './connection-applied-info-cell/connection-applied-info-cell';
import ConnectionNameCell from './connection-name-cell/connection-name-cell';
import { Checkbox, Tooltip } from 'antd';
import { useNavigate } from 'src/utils/use-navigate';
import { ConnectionModel, isInitialPolicyModeUndefined } from '../connection.model';
import Sort from '@svg/sort.svg?react';
import { Player } from '@lottiefiles/react-lottie-player';
import loaderAnimation from '../../../../assets/lottie/md-loader.json';
import { NoResult } from '../../../shared/no-result/no-result';
import { sortByDate } from '../../../../utils/datesUtils';
import EcmBulkActions from './ecm-bulk-actions/ecm-bulk-actions';
import EcmInlineActions from './ecm-inline-actions/ecm-inline-actions';
import { ECMActionType, LevelName} from '../../../../utils/enums';
import { PopoverGotIt } from '../../../shared/popover/popover-got-it';
import { useStores } from '../../../store';
import EcmModals from './ecm-modals';
import customPolicyImg from 'src/assets/svg/custom-popover-img.png';

const ROW_HEIGHT = 72;

const ConnectionTable: React.FC<{
    connections?: ConnectionModel[],
    selectedConnections: Map<number, ConnectionModel>,
    setSelectedConnections: React.Dispatch<React.SetStateAction<Map<number, ConnectionModel>>>,
    gotItPopoverConnectionId?: number | null,
    onCloseGotItPopover?: () => void
}> = memo(({connections, selectedConnections, setSelectedConnections, gotItPopoverConnectionId, onCloseGotItPopover}) => {

    const { customerStore } = useStores();

    const [sortField, setSortField] = useState<'date' | 'policyStatus'>('date');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [scrollTop, setScrollTop] = useState(0);
    const [visibleRowCount, setVisibleRowCount] = useState(0);
    const [modalState, setModalState] = useState<{
        type?: 'policyActions' | 'policySummary' | 'cancelSmart' | 'editConnection' | 'editStandardName',
        actionType?: ECMActionType,
        connection?: ConnectionModel
    }>({});
    const tableBodyRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const calculateVisibleRowCount = () => {
            if (tableBodyRef.current) {
                setVisibleRowCount(Math.ceil((tableBodyRef.current.clientHeight || 400) / ROW_HEIGHT));
            }
        };

        calculateVisibleRowCount();
        window.addEventListener('resize', calculateVisibleRowCount);

        return () => {
            window.removeEventListener('resize', calculateVisibleRowCount);
        };
    }, [connections]);

    const toggleSelection = (connection: ConnectionModel) => {
        setSelectedConnections(prev => {
            const newSelected = new Map(prev);
            if (newSelected.has(connection.id)) {
                newSelected.delete(connection.id);
            } else {
                newSelected.set(connection.id, connection);
            }
            return newSelected;
        });
    };

    const sortedConnections = useMemo(() => {
        if (!connections) return [];
        const conns = [...connections];
        if (sortField === 'date') {
            return conns.sort((a, b) => sortByDate(sortOrder, a.policyApplyDate, b.policyApplyDate));
        } else if (sortField === 'policyStatus') {
            return conns.sort((a, b) => {
                const aStatus = a.policyStatus || '';
                const bStatus = b.policyStatus || '';
                if (aStatus < bStatus) return sortOrder === 'asc' ? -1 : 1;
                if (aStatus > bStatus) return sortOrder === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return conns;
    }, [connections, sortOrder, sortField]);

    const allSelectableConnecions = useMemo(() => connections?.filter(conn => conn.isSelectable), [connections]);
    const isAllSelected = selectedConnections.size > 0 && allSelectableConnecions?.length === selectedConnections.size;
    const isIndeterminate = selectedConnections.size > 0 && !isAllSelected;

    const handleRowKeyDown = (e: React.KeyboardEvent, connection: ConnectionModel) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            void handleRowClick(connection);
        }
    };

    const startIndex = Math.floor(scrollTop / ROW_HEIGHT);
    const endIndex = startIndex + visibleRowCount;
    const visibleConnections = sortedConnections.slice(startIndex, Math.min(endIndex, sortedConnections.length));

    const handleScroll = () => {
        if (tableBodyRef.current) {
            setScrollTop(tableBodyRef.current.scrollTop);
        }
    };

    const handleRowClick = async (connection: ConnectionModel) => {
        const isBronze = connection.serviceLevel === LevelName.BRONZE;
        if (!isBronze) {
            if(isInitialPolicyModeUndefined(connection)) {
                await customerStore.setCustomPolicy([connection.id]);
            } else {
                const service = customerStore.selectedCustomer?.services.find(s => s.serviceId === connection.id);
                if (service) {
                    customerStore.setSelectedLocation({ ...service, policyMode: connection.policyMode });
                }
            }
        }
        navigate(`../${connection.id}/dashboard${isBronze ? '-bronze' : ''}`);
    };

    // Extracted sort handler
    const handleSort = (field: 'date' | 'policyStatus') => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder(field === 'date' ? 'desc' : 'asc');
        }
    };

    return (
        <div className="ecm-table">
            <EcmBulkActions selectedConnections={selectedConnections}
                            setSelectedConnections={setSelectedConnections}/>
            <div className="thead">
                <div className="tr">
                    <div className="th">
                        <Checkbox
                            indeterminate={isIndeterminate}
                            checked={isAllSelected}
                            onChange={() =>
                                setSelectedConnections(isAllSelected ?
                                    new Map() :
                                    new Map(allSelectableConnecions?.map(conn => [conn.id, conn])))}
                        />
                    </div>
                    <div className="th">Connection Name</div>
                    <div className="th">Type</div>
                    <div className="th">Plan</div>
                    <button type="button" className="th" onClick={() => handleSort('policyStatus')}>
                        Policy
                        {sortField === 'policyStatus' &&
                            <Sort className={`sort-icon ${sortOrder}`}/>
                        }
                    </button>
                    <button type="button" className="th" onClick={() => handleSort('date')}>
                        Applied
                        {sortField === 'date' &&
                            <Sort className={`sort-icon ${sortOrder}`}/>
                        }
                    </button>
                </div>
            </div>
            {!connections?.length && (
                <div className="table-data-indicator">
                    {!connections && <Player autoplay loop src={loaderAnimation}/>}
                    {!!connections && !connections.length &&
                        <NoResult title="No Results Found" subTitle="Change the search criteria and try again."/>}
                </div>
            )}
            {!!connections?.length && (
                <div className="tbody" ref={tableBodyRef} onScroll={handleScroll} style={{position: 'relative'}}>
                    <div className="tbody-scroller"
                         style={{height: `${sortedConnections.length * ROW_HEIGHT || 400}px`, position: 'relative'}}>
                        {visibleConnections.map((connection, index) => (
                            <div
                                className={`tr ${selectedConnections.has(connection.id) ? 'selected' : ''}`}
                                key={connection.id}
                                style={{
                                    position: 'absolute',
                                    top: `${(startIndex + index) * ROW_HEIGHT}px`,
                                    height: `${ROW_HEIGHT}px`,
                                    width: '100%'
                                }}
                            >
                                <div className="td">
                                    <Tooltip title={connection?.disableReason}
                                             overlayClassName="tooltip-wrapper disable-tooltip"
                                             placement="bottom"
                                             align={{
                                                 offset: [0, -25],
                                             }}>
                                        <Checkbox
                                            disabled={!connection.isSelectable}
                                            checked={selectedConnections.has(connection.id)}
                                            onChange={() => toggleSelection(connection)}
                                        />
                                    </Tooltip>
                                </div>
                                <button type="button" className="td"
                                     onClick={() => handleRowClick(connection)}
                                     onKeyDown={(e) => handleRowKeyDown(e, connection)}>
                                    <PopoverGotIt
                                        open={gotItPopoverConnectionId === connection.id}
                                        placement="bottomLeft"
                                        overlayClassName="ecm-got-it-popover"
                                        title="Customize your policy"
                                        text="To enter any connection, click its name"
                                        content={<img src={customPolicyImg} alt="Custom Policy" />}
                                        onClickGotIt={onCloseGotItPopover}>
                                            <ConnectionNameCell connection={connection}/>
                                    </PopoverGotIt>
                                </button>
                                <button type="button" className="td"
                                     onClick={() => handleRowClick(connection)}
                                     onKeyDown={(e) => handleRowKeyDown(e, connection)}>
                                    <ConnectionTypeCell connection={connection}/></button>
                                <button type="button" className="td"
                                     onClick={() => handleRowClick(connection)}
                                     onKeyDown={(e) => handleRowKeyDown(e, connection)}>
                                    <ConnectionPlanCell connection={connection}/></button>
                                <div className="td">
                                    <ConnectionPolicyInfoCell connection={connection}/></div>
                                <div className="td"><ConnectionAppliedInfoCell connection={connection}/></div>
                                <EcmInlineActions
                                    connection={connection}
                                    setModalState={setModalState}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <EcmModals
                modalState={modalState}
                setModalState={setModalState}
            />
        </div>
    );
});

export default ConnectionTable;
